import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, PlayCircle, RefreshCw, Search, Square, Target, Store } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useIsPrivileged } from '@/hooks/useAuth';
import { useCancelJob } from '@/modules/sales-corp/api';
import {
  useSalesOutlets, useCompanies, useResearchRuns, useRunResearch, useRunWarmSignal, useWarmProgress,
} from './api';
import { toRow, scoreColor, scoreBand, CATEGORY_LABEL, formatDistance } from './lib';
import type { CompanyCategory, CompanyRow } from './types';
import { ResearchMap } from './ResearchMap';
import { KioskTab } from './KioskTab';

type SortKey = 'score' | 'distance' | 'warm';
type View = 'targets' | 'kiosk';

export function ResearchPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const outletsQ = useSalesOutlets();
  const companiesQ = useCompanies();

  const [outletId, setOutletId] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [warmOnly, setWarmOnly] = useState(false);
  const [minScore, setMinScore] = useState(0);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [dryRun, setDryRun] = useState(false);
  const [view, setView] = useState<View>('targets');

  const runResearch = useRunResearch();
  const runWarm = useRunWarmSignal();
  const cancelJob = useCancelJob();
  const privileged = useIsPrivileged();
  const runsQ = useResearchRuns(outletId || undefined);
  const warmProgress = useWarmProgress();

  const outlets = useMemo(() => outletsQ.data ?? [], [outletsQ.data]);
  const cities = useMemo(
    () => Array.from(new Set(outlets.map((o) => o.city).filter(Boolean))) as string[],
    [outlets],
  );
  const outletName = useMemo(
    () => Object.fromEntries(outlets.map((o) => [o.outlet_id, o.name])),
    [outlets],
  );
  const visibleOutlets = useMemo(
    () => (city ? outlets.filter((o) => o.city === city) : outlets),
    [outlets, city],
  );

  const rows = useMemo(() => {
    const all = (companiesQ.data ?? [])
      .map((c) => toRow(c, outletId || undefined))
      .filter((r): r is CompanyRow => r != null);
    let filtered = all;
    if (city && !outletId) {
      filtered = filtered.filter((r) => r.primary_city === city);
    }
    if (category) filtered = filtered.filter((r) => r.category === category);
    if (warmOnly) filtered = filtered.filter((r) => r.orders_last_90d > 0);
    if (minScore > 0) filtered = filtered.filter((r) => (r.score ?? 0) >= minScore);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter((r) => r.name.toLowerCase().includes(q));
    }
    return filtered.sort((a, b) => {
      if (sortKey === 'distance') return (a.nearest_distance_m ?? 9e9) - (b.nearest_distance_m ?? 9e9);
      if (sortKey === 'warm') return b.orders_last_90d - a.orders_last_90d;
      return (b.score ?? -1) - (a.score ?? -1);
    });
  }, [companiesQ.data, outletId, city, category, warmOnly, minScore, search, sortKey]);

  const lastRun = runsQ.data?.[0];
  const isRunning = runResearch.isPending || runWarm.isPending;

  async function invalidate() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['sales', 'companies'] }),
      qc.invalidateQueries({ queryKey: ['sales', 'runs'] }),
      qc.invalidateQueries({ queryKey: ['sales', 'warm-progress'] }),
    ]);
  }

  function doResearch() {
    const body: Record<string, unknown> = { dry_run: dryRun, max_pages: 2 };
    if (outletId) body.outlet_id = outletId;
    else if (city) body.city = city;
    runResearch.mutate(body, { onSuccess: invalidate });
  }
  function doWarm() {
    runWarm.mutate({ scan_days: 400, warm_days: 90, geocode_limit: 150 }, { onSuccess: invalidate });
  }

  const summary = useMemo(() => {
    const scored = rows.filter((r) => r.score != null);
    return {
      count: rows.length,
      warm: rows.filter((r) => r.orders_last_90d > 0).length,
      hot: rows.filter((r) => (r.score ?? 0) >= 75).length,
      avg: scored.length ? Math.round(scored.reduce((s, r) => s + (r.score ?? 0), 0) / scored.length) : 0,
    };
  }, [rows]);

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Research</h1>
          <p className="text-sm text-muted-foreground">
            {view === 'targets'
              ? 'Corporate targets around each outlet — ranked, deduplicated, explainable.'
              : 'High-footfall corporate parks & office hubs for Flax kiosks — Mumbai & Bengaluru.'}
          </p>
        </div>
        {view === 'targets' && (
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            {privileged ? (
              <>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
                  Dry run
                </label>
                {isRunning && (
                  <Button size="sm" variant="destructive" onClick={() => cancelJob.mutate('research')} disabled={cancelJob.isPending}>
                    <Square className="h-3.5 w-3.5" /> Stop
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={doWarm} disabled={isRunning}>
                  {runWarm.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Warm signal
                </Button>
                <Button size="sm" onClick={doResearch} disabled={isRunning}>
                  {runResearch.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                  Run research{outletId ? '' : city ? ` · ${city}` : ' · all'}
                </Button>
              </>
            ) : (
              <span className="text-2xs text-muted-foreground">Research runs are triggered by Gurmeet.</span>
            )}
          </div>
          {warmProgress.data && warmProgress.data.total > 0 && (
            <span className="text-2xs text-muted-foreground">
              Warm geocoding: {warmProgress.data.total - warmProgress.data.pending}/{warmProgress.data.total}
              {warmProgress.data.pending > 0
                ? ` · ${warmProgress.data.pending} pending — run Warm signal again`
                : ' · complete'}
            </span>
          )}
        </div>
        )}
      </div>

      {/* view tabs */}
      <div className="flex items-center gap-1 border-b border-border/60">
        <TabButton active={view === 'targets'} onClick={() => setView('targets')} icon={Target}>Targets</TabButton>
        <TabButton active={view === 'kiosk'} onClick={() => setView('kiosk')} icon={Store}>Kiosk sites</TabButton>
      </div>

      {view === 'kiosk' ? <KioskTab /> : <>

      {/* run status line */}
      {(isRunning || runResearch.data || runWarm.data || runResearch.error || runWarm.error) && (
        <div className="rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs">
          {isRunning && <span className="text-muted-foreground">Running… this can take up to a few minutes. Hit <strong>Stop</strong> to cancel.</span>}
          {!isRunning && runResearch.data && (
            <span className="text-foreground">
              Research done: {JSON.stringify(
                (runResearch.data.results as Array<{ outlet?: string; companies_found?: number; companies_new?: number }> | undefined)
                  ?.map((r) => ({ outlet: r.outlet, found: r.companies_found, new: r.companies_new })) ?? runResearch.data,
              )}
            </span>
          )}
          {!isRunning && runWarm.data && (
            <span className="text-foreground">
              Warm signal: {runWarm.data.addresses_upserted} addresses · {runWarm.data.geocoded} geocoded · {runWarm.data.locations_matched} locations matched · {runWarm.data.companies_scored} scored
            </span>
          )}
          {(runResearch.error || runWarm.error) && (
            <span className="text-[#B15599]">{String((runResearch.error || runWarm.error as Error))}</span>
          )}
        </div>
      )}

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={city} onChange={(v) => { setCity(v); setOutletId(''); }} placeholder="All cities"
          options={cities.map((c) => ({ value: c, label: c }))} />
        <Select value={outletId} onChange={setOutletId} placeholder="All outlets"
          options={visibleOutlets.map((o) => ({ value: o.outlet_id, label: o.name }))} />
        <Select value={category} onChange={setCategory} placeholder="All categories"
          options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} />
        <Select value={String(minScore)} onChange={(v) => setMinScore(Number(v))} placeholder="Min score"
          options={[0, 35, 55, 75].map((n) => ({ value: String(n), label: n === 0 ? 'Any score' : `Score ≥ ${n}` }))} />
        <label className="flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1.5 text-sm">
          <input type="checkbox" checked={warmOnly} onChange={(e) => setWarmOnly(e.target.checked)} />
          Warm only
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company…"
            className="h-9 rounded-md border border-border/60 bg-transparent pl-7 pr-2 text-sm outline-none focus:border-[#51BFAA]"
          />
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>{summary.count} companies</span>·<span>{summary.warm} warm</span>·
          <span>{summary.hot} hot</span>·<span>avg {summary.avg}</span>
        </div>
      </div>

      {/* map */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[420px] w-full overflow-hidden rounded-xl">
            {outletsQ.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResearchMap
                outlets={outlets}
                rows={rows}
                selectedOutletId={outletId || undefined}
                onSelectCompany={(id) => navigate(`/research/${id}`)}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
            <span className="text-sm font-medium">Companies</span>
            <Select value={sortKey} onChange={(v) => setSortKey(v as SortKey)} placeholder="Sort"
              options={[
                { value: 'score', label: 'Sort: Score' },
                { value: 'distance', label: 'Sort: Distance' },
                { value: 'warm', label: 'Sort: Warm orders' },
              ]} />
          </div>
          {companiesQ.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
              <Building2 className="h-6 w-6" />
              No companies yet. Pick an outlet or city and hit <strong>Run research</strong>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr className="border-b border-border/60">
                    <th className="px-3 py-2 font-medium">Company</th>
                    <th className="px-3 py-2 font-medium">Category</th>
                    <th className="px-3 py-2 font-medium">Score</th>
                    <th className="px-3 py-2 font-medium">Nearest outlet</th>
                    <th className="px-3 py-2 font-medium">Distance</th>
                    <th className="px-3 py-2 font-medium">Warm 90d</th>
                    <th className="px-3 py-2 font-medium">Zoho</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 500).map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/research/${r.id}`)}
                      className="cursor-pointer border-b border-border/40 hover:bg-accent/50"
                    >
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="font-normal">{CATEGORY_LABEL[r.category as CompanyCategory]}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="inline-flex min-w-[2.5rem] items-center justify-center rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold text-white"
                          style={{ backgroundColor: scoreColor(r.score) }}
                          title={scoreBand(r.score)}
                        >
                          {r.score ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.nearest_outlet_id ? outletName[r.nearest_outlet_id] ?? '—' : '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{formatDistance(r.nearest_distance_m)}</td>
                      <td className="px-3 py-2">
                        {r.orders_last_90d > 0
                          ? <span className="font-mono text-xs font-semibold text-[#51BFAA]">{r.orders_last_90d}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2">{r.exists_in_zoho ? <Badge variant="outline">in CRM</Badge> : <span className="text-muted-foreground">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* recent runs */}
      {lastRun && (
        <p className="text-xs text-muted-foreground">
          Last run: {new Date(lastRun.started_at).toLocaleString()} · {lastRun.status}
          {lastRun.companies_found ? ` · ${lastRun.companies_found} found (${lastRun.companies_new} new)` : ''}
          {lastRun.cost_estimate_usd ? ` · ~$${lastRun.cost_estimate_usd} API` : ''}
        </p>
      )}
      </>}
    </div>
  );
}

// Tab button for the Targets / Kiosk sites switcher.
function TabButton({ active, onClick, icon: Icon, children }: {
  active: boolean; onClick: () => void; icon: typeof Target; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active ? 'border-[#51BFAA] text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}

// Small styled native select to avoid pulling in a new dependency.
function Select({
  value, onChange, options, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-9 rounded-md border border-border/60 bg-transparent px-2 text-sm outline-none focus:border-[#51BFAA]',
      )}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
